import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Typography } from '@/components/ui/typography'
import { formatCurrency } from '@/lib/utils'
import { Check, Coins } from 'lucide-react'
import { useId, useState } from 'react'

export const CASH_DENOMINATION_GROUPS = [
  {
    id: 'bills',
    label: 'Bills',
    denominations: [
      { id: 'bill-100', label: '$100 bill', shortLabel: '$100', cents: 10000 },
      { id: 'bill-50', label: '$50 bill', shortLabel: '$50', cents: 5000 },
      { id: 'bill-20', label: '$20 bill', shortLabel: '$20', cents: 2000 },
      { id: 'bill-10', label: '$10 bill', shortLabel: '$10', cents: 1000 },
      { id: 'bill-5', label: '$5 bill', shortLabel: '$5', cents: 500 },
      { id: 'bill-1', label: '$1 bill', shortLabel: '$1', cents: 100 },
    ],
  },
  {
    id: 'coins',
    label: 'Coins',
    denominations: [
      { id: 'coin-25', label: 'Quarter', shortLabel: 'Quarters', cents: 25 },
      { id: 'coin-10', label: 'Dime', shortLabel: 'Dimes', cents: 10 },
      { id: 'coin-5', label: 'Nickel', shortLabel: 'Nickels', cents: 5 },
      { id: 'coin-1', label: 'Penny', shortLabel: 'Pennies', cents: 1 },
    ],
  },
]

const CASH_DENOMINATIONS = CASH_DENOMINATION_GROUPS.flatMap((group) => group.denominations)
const MAX_DENOMINATION_QUANTITY = 9999

export function calculateCashCountCents(counts) {
  return CASH_DENOMINATIONS.reduce((total, denomination) => {
    const quantity = Number(counts[denomination.id] || 0)
    const isValidQuantity =
      Number.isInteger(quantity) && quantity > 0 && quantity <= MAX_DENOMINATION_QUANTITY

    return total + (isValidQuantity ? quantity * denomination.cents : 0)
  }, 0)
}

// Draft quantities stay local; only Use this total changes the opening/closing cash field.
export function CashDenominationCounter({
  onChange,
  triggerVariant = 'outline',
  triggerSize = 'sm',
}) {
  const [open, setOpen] = useState(false)
  const [counts, setCounts] = useState({})
  const [draftCounts, setDraftCounts] = useState({})
  const counterId = useId()
  const totalCents = calculateCashCountCents(draftCounts)
  const hasCount = Object.values(draftCounts).some((quantity) => quantity !== '')

  const applyCount = () => {
    if (!hasCount) return
    setCounts(draftCounts)
    onChange((totalCents / 100).toFixed(2))
    setOpen(false)
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (nextOpen) setDraftCounts(counts)
        setOpen(nextOpen)
      }}
    >
      <DialogTrigger asChild>
        <Button type="button" variant={triggerVariant} size={triggerSize}>
          <Coins className="size-4" />
          Count by bills and coins
        </Button>
      </DialogTrigger>
      <DialogContent size="large" viewport="scroll" className="@container">
        <DialogHeader>
          <DialogTitle>Count by bills and coins</DialogTitle>
          <DialogDescription>
            Enter how many of each you have. Use the total when you finish counting cash. Checks are
            counted separately.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-6 @lg:grid-cols-2">
          {CASH_DENOMINATION_GROUPS.map((group) => (
            <section
              key={group.id}
              aria-labelledby={`${counterId}-${group.id}`}
              className="min-w-0"
            >
              <Typography
                variant="labelStrong"
                id={`${counterId}-${group.id}`}
                className="mb-2 block"
              >
                {group.label}
              </Typography>
              <div className="divide-y divide-border">
                {group.denominations.map((denomination) => {
                  const quantity = draftCounts[denomination.id] ?? ''
                  const inputId = `${counterId}-${denomination.id}`

                  return (
                    <div
                      key={denomination.id}
                      className="grid grid-cols-[minmax(0,1fr)_4rem_5.5rem] items-center gap-2 py-2"
                    >
                      <Label htmlFor={inputId} weight="normal">
                        {denomination.shortLabel}
                      </Label>
                      <Input
                        id={inputId}
                        aria-label={`${denomination.label} quantity`}
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]{0,4}"
                        maxLength={4}
                        value={quantity}
                        placeholder="0"
                        className="text-right tabular-nums"
                        onChange={(event) => {
                          const value = event.target.value
                          if (!/^\d{0,4}$/.test(value)) return
                          setDraftCounts({ ...draftCounts, [denomination.id]: value })
                        }}
                        onKeyDown={(event) => {
                          if (event.key !== 'Enter') return
                          event.preventDefault()
                          event.stopPropagation()
                          applyCount()
                        }}
                      />
                      <Typography variant="bodySmall" align="right" className="tabular-nums">
                        {formatCurrency((Number(quantity || 0) * denomination.cents) / 100)}
                      </Typography>
                    </div>
                  )
                })}
              </div>
            </section>
          ))}
        </div>
        <Card size="flush" surface="brandSubtle">
          <CardContent className="flex items-center justify-between gap-3 py-4">
            <Typography variant="labelStrong">Cash total</Typography>
            <Typography variant="h3" className="tabular-nums" aria-live="polite">
              {formatCurrency(totalCents / 100)}
            </Typography>
          </CardContent>
        </Card>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Button type="button" variant="ghost" size="sm" onClick={() => setDraftCounts({})}>
            Clear counts
          </Button>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </DialogClose>
            <Button type="button" disabled={!hasCount} onClick={applyCount}>
              <Check className="size-4" />
              Use this total
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  )
}
